import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import type { DeliveryBoardStatus, SiteLogistics } from '../interfaces/ILogistics.js';
import {
  createLogisticsRevisionStore,
  isStale,
  type ILogisticsRevisionStore,
} from '../logistics/revisionStore.js';
import { logisticsKeys } from './logisticsKeys.js';

/** ADR-0014's detection: a 30 s probe while the page is in front, plus one on return. */
export const LOGISTICS_REVISION_POLL_MS = 30_000;

/** A read the page shows beyond logistics and statuses (the web's site map). */
export interface ILogisticsExtraRead {
  queryKey: QueryKey;
  fetch: (siteId: string) => Promise<unknown>;
}

export interface IUseLogisticsRevisionArgs {
  siteId: string;
  /** The revision on the page's `logisticsKeys.page(siteId)` payload. */
  loadedRevision: number | undefined;
  /** That query's `isFetchedAfterMount`: the payload came from this page's own
   *  fetch, not from a cache another screen filled earlier and which may be old. */
  fetchedAfterMount: boolean;
  enabled: boolean;
  /** The app's bound reads (injected: the transport differs per app). */
  fetchers: {
    revision: (siteId: string, signal?: AbortSignal) => Promise<number>;
    logistics: (siteId: string) => Promise<SiteLogistics>;
    statuses: (siteId: string) => Promise<DeliveryBoardStatus[]>;
  };
  /** Further reads the page holds, reloaded together with the two above. */
  extraReads?: ILogisticsExtraRead[];
}

export interface IUseLogisticsRevisionResult {
  revision: ILogisticsRevisionStore;
  /** The page holds a revision to send. Until then the page is read-only. */
  ready: boolean;
  stale: boolean;
  /** "Load" is running. */
  reloading: boolean;
  /** Rejects when a read fails; the lock then stays. */
  reload: () => Promise<void>;
}

/**
 * The Logistics page's refresh-to-edit state (spec
 * 2026-09-29-logistics-refresh-to-edit), shared by web and mobile.
 *
 * The held revision moves only three ways: the page's first load, "Load
 * latest version", and the page's own write answers. NOT on a background
 * refetch of the logistics key — that key is shared with other screens and
 * refetches on focus, and adopting its revision would lift the lock while the
 * statuses on screen are still old. And the first load counts only once this
 * page's own fetch has landed (`fetchedAfterMount`): a cached payload may be old.
 */
export function useLogisticsRevision({
  siteId,
  loadedRevision,
  fetchedAfterMount,
  enabled,
  fetchers,
  extraReads = [],
}: IUseLogisticsRevisionArgs): IUseLogisticsRevisionResult {
  const queryClient = useQueryClient();
  // One store per site: a site switch starts from that site's first load. The
  // store is re-created during render (never discarded by a memo) with the
  // first load as its seed, so there is no effect and no second render pass
  // for the seed itself.
  const [entry, setEntry] = useState(() => ({ siteId, store: createLogisticsRevisionStore() }));
  const firstLoad = fetchedAfterMount ? loadedRevision : undefined;
  let store = entry.store;
  if (entry.siteId !== siteId || (firstLoad !== undefined && store.getSnapshot().loadedRevision === null)) {
    store = createLogisticsRevisionStore(firstLoad ?? null);
    setEntry({ siteId, store });
  }
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const poll = useQuery({
    queryKey: logisticsKeys.revision(siteId),
    queryFn: ({ signal }) => fetchers.revision(siteId, signal),
    enabled,
    // The global default staleTime would swallow the on-focus check.
    staleTime: 0,
    refetchInterval: LOGISTICS_REVISION_POLL_MS,
    refetchOnWindowFocus: true,
  });
  // Handed to the store so the end of a write can re-check without a request.
  useEffect(() => {
    if (poll.data !== undefined) store.observe(poll.data);
  }, [store, poll.data]);
  const [reloading, setReloading] = useState(false);
  const pendingReload = useRef<Promise<void> | null>(null);
  const cancelPageQueries = () =>
    Promise.all(
      [logisticsKeys.page(siteId), logisticsKeys.statuses(siteId), ...extraReads.map((read) => read.queryKey)].map(
        (queryKey) => queryClient.cancelQueries({ queryKey }),
      ),
    );
  const runReload = async () => {
    // An older refetch still in flight would land after the fresh data below
    // and put the old data back under an unlocked page.
    await cancelPageQueries();
    // Fetched directly, not through the page's queries: a failed reload must
    // not put those into error (the page would unmount an open form), and the
    // lock must not lift over old data. The revision-bearing payload goes
    // first, so everything read after it is at least as new.
    const fresh = await fetchers.logistics(siteId);
    const [statuses, ...extras] = await Promise.all([
      fetchers.statuses(siteId),
      ...extraReads.map((read) => read.fetch(siteId)),
    ]);
    // A refetch may have started while these were in flight: cancel it too.
    await cancelPageQueries();
    // Only when ALL reads landed does the cache move.
    queryClient.setQueryData(logisticsKeys.page(siteId), fresh);
    queryClient.setQueryData(logisticsKeys.statuses(siteId), statuses);
    extraReads.forEach((read, index) => queryClient.setQueryData(read.queryKey, extras[index]));
    store.load(fresh.revision);
    if (enabled) void poll.refetch();
  };
  // Two taps are one reload.
  const reload = () => {
    if (pendingReload.current) return pendingReload.current;
    setReloading(true);
    const pending = runReload().finally(() => {
      pendingReload.current = null;
      setReloading(false);
    });
    pendingReload.current = pending;
    return pending;
  };
  return {
    revision: store,
    ready: snapshot.loadedRevision !== null,
    stale: isStale(snapshot, poll.data, store.isWriteInFlight()),
    reloading,
    reload,
  };
}
